import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Receipt } from 'lucide-react';

const RefundPolicy = () => {
  return (
    <div className="space-y-6" data-testid="refund-policy-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
          <Receipt className="h-7 w-7 text-amber-400" />
          Refund Policy
        </h1>
        <p className="text-slate-400 mt-1 text-sm">When and how refunds apply to your Seafarer Logbook subscription</p>
      </div>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">1. Your Right to Cancel (UK Cooling-Off)</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>Under the UK Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013, you have the right to cancel a digital-services subscription within <strong className="text-slate-300">14 days</strong> of purchase and receive a full refund — provided you have not yet started using the paid features within that period.</p>
          <p>Because Seafarer Logbook delivers the paid service immediately on subscribing (instant access to premium features), you will be asked to <strong className="text-slate-300">acknowledge at checkout</strong> that you consent to the service starting straight away and understand that doing so waives the 14-day cooling-off right for any portion of the service already consumed.</p>
          <p>If you have NOT used the paid features at all within the first 14 days, you may request a full refund by emailing <a href="mailto:admin@seafarerlogbook.co.uk" className="text-amber-400 hover:text-amber-300">admin@seafarerlogbook.co.uk</a>.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">2. Cancelling Your Subscription</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>You can cancel your subscription at any time from the <strong className="text-slate-300">Settings page</strong> in the app or via the Stripe customer portal link sent to your email.</p>
          <p>Cancellation takes effect at the end of your current billing period. You will continue to have full paid access until that date. <strong className="text-slate-300">No automatic refund</strong> is issued for the unused portion of the current period.</p>
          <p>You will not be charged again after cancellation.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">3. Refund Stance Beyond the Cooling-Off Period</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>Outside the 14-day cooling-off window, subscriptions are <strong className="text-slate-300">non-refundable</strong> as a matter of policy. We do not pro-rate refunds for the unused portion of a billing period if you cancel mid-cycle.</p>
          <p>However, we will consider discretionary refunds in genuine cases, including:</p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li>A duplicate charge or billing error caused by us or by Stripe</li>
            <li>A serious, prolonged service outage that prevented you from using the Service for the majority of a billing period</li>
            <li>A material misrepresentation of features on our part that you reasonably relied on when subscribing</li>
            <li>Other exceptional circumstances — at our reasonable discretion</li>
          </ul>
          <p>Discretionary refund requests should be made by email to <a href="mailto:admin@seafarerlogbook.co.uk" className="text-amber-400 hover:text-amber-300">admin@seafarerlogbook.co.uk</a> within 30 days of the charge, stating the reason. We will respond within 5 working days.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">4. How Refunds Are Issued</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>Approved refunds are issued via <strong className="text-slate-300">Stripe</strong> to the original payment method used for the purchase. Refunds typically appear within 5&ndash;10 working days, though the exact timing depends on your bank or card issuer.</p>
          <p>We do not offer refunds in cash, vouchers, or to a different payment method.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">5. Chargebacks</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>If you believe a charge is incorrect, please contact us at <a href="mailto:admin@seafarerlogbook.co.uk" className="text-amber-400 hover:text-amber-300">admin@seafarerlogbook.co.uk</a> first so we can resolve it directly &mdash; most issues are sorted within a few days.</p>
          <p>If you initiate a chargeback without contacting us first and the chargeback is found by the card issuer to be invalid, we reserve the right to suspend your account and recover the disputed amount plus reasonable handling costs.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">6. Statutory Rights Not Affected</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>Nothing in this Refund Policy affects your statutory rights as a consumer under UK law. If you believe the Service is not as described, not fit for purpose, or not of satisfactory quality, you may have rights under the Consumer Rights Act 2015 that go beyond the discretionary refunds described above.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">7. Contact</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>All refund and cancellation queries: <a href="mailto:admin@seafarerlogbook.co.uk" className="text-amber-400 hover:text-amber-300">admin@seafarerlogbook.co.uk</a></p>
          <p className="text-xs text-slate-500 mt-3">Last updated: April 2026.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default RefundPolicy;
